import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Injector, inject } from '@angular/core';
import { CLIENT_TOKEN } from '@/app/core/constants/auth.constants';
import { environment } from '@/environments/environment';
import { catchError, throwError } from 'rxjs';
import { AccessDeniedNotificationService } from '@core/services/access-denied-notification.service';
import { PlanLimitNotificationService } from '@core/services/plan-limit-notification.service';
import { ServerErrorNotificationService } from '@core/services/server-error-notification.service';
import { AuthService } from '@core/services/auth.service';
import { Router } from '@angular/router';

const normalizeBaseUrl = (value: string): string =>
  value.trim().replace(/\/$/, '');

// HttpContext survives subscriptions/retries of the same request; new requests get a new key.
export const AI_REQUEST_KEY = new HttpContextToken<string>(() => '');

export const authTokenInterceptor: HttpInterceptorFn = (req, next) => {
  const baseUrl = normalizeBaseUrl(environment.apiBaseUrl ?? '');
  const requestUrl = req.url.trim();
  if (
    baseUrl &&
    requestUrl !== baseUrl &&
    !requestUrl.startsWith(`${baseUrl}/`)
  ) {
    return next(req);
  }

  const injector = inject(Injector);

  const token = localStorage.getItem(CLIENT_TOKEN);
  let authorizedRequest =
    token && baseUrl && !req.headers.has('Authorization')
      ? req.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`,
          },
        })
      : req;

  if (baseUrl && token && ['POST', 'PUT', 'PATCH'].includes(req.method)) {
    let key = req.context.get(AI_REQUEST_KEY);
    if (!key) {
      key = crypto.randomUUID();
      req.context.set(AI_REQUEST_KEY, key);
    }
    if (!authorizedRequest.headers.has('Idempotency-Key')) {
      authorizedRequest = authorizedRequest.clone({ setHeaders: { 'Idempotency-Key': key } });
    }
  }

  return next(authorizedRequest).pipe(
    catchError((error: unknown) => {
      const apiPath = requestUrl
        .slice(baseUrl.length)
        .replace(/^\//, '')
        .split('?')[0];
      const publicAuthenticationRequest = /^(login|register|auth\/)/.test(
        apiPath,
      );
      if (
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        !publicAuthenticationRequest &&
        token &&
        authorizedRequest.headers.get('Authorization') === `Bearer ${token}` &&
        localStorage.getItem(CLIENT_TOKEN) === token
      ) {
        const auth = injector.get(AuthService);
        const returnUrl = injector.get(Router).url;
        auth.logout();
        auth.handleLogin(returnUrl);
      }

      if (
        error instanceof HttpErrorResponse &&
        (error.status === 402 || error.status === 429)
      ) {
        injector.get(PlanLimitNotificationService).handleHttpError(error);
      }

      if (error instanceof HttpErrorResponse && error.status === 403) {
        injector.get(AccessDeniedNotificationService).show();
      }

      if (error instanceof HttpErrorResponse && error.status >= 500) {
        injector.get(ServerErrorNotificationService).show();
      }

      return throwError(() => error);
    }),
  );
};
